import type { ReactNode } from 'react'
import Dialog from '@mui/material/Dialog'
import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import { confirmationBackdropSx, confirmationPaperSx } from './confirmationModalStyles'

type Props = Readonly<{
  open: boolean
  onClose?: (event: object, reason: 'backdropClick' | 'escapeKeyDown') => void
  dialogTestId?: string
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  disableBackdropClose?: boolean
  disableEscapeKeyDown?: boolean
  paperSx?: SxProps<Theme>
  backdropSx?: SxProps<Theme>
  children: ReactNode
}>

export function ConfirmationModalShell({
  open,
  onClose,
  dialogTestId,
  ariaLabelledBy,
  ariaDescribedBy,
  disableBackdropClose = false,
  disableEscapeKeyDown = false,
  paperSx,
  backdropSx,
  children,
}: Props) {
  const handleClose = (
    event: object,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (disableBackdropClose && reason === 'backdropClick') return
    if (disableEscapeKeyDown && reason === 'escapeKeyDown') return
    onClose?.(event, reason)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose ? handleClose : undefined}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      data-testid={dialogTestId}
      slotProps={{
        backdrop: { sx: { ...confirmationBackdropSx, ...backdropSx } },
      }}
      PaperProps={{ elevation: 0, sx: { ...confirmationPaperSx, ...paperSx } }}
    >
      {children}
    </Dialog>
  )
}

export function ConfirmationModalLayout({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: '32px',
      }}
    >
      {children}
    </Box>
  )
}
